import React, { useState, useMemo } from 'react';
import {
  Users,
  UserCheck,
  UserCog,
  MessageSquare,
  Bell,
  ChevronDown,
  ChevronUp,
  AlertTriangle,
  Building2,
  Shield,
  Laptop,
  Briefcase,
  Scale,
  HelpCircle
} from 'lucide-react';

interface StakeholderRole {
  id: string;
  name: string;
  department: string;
  role_type: 'process_owner' | 'it' | 'compliance' | 'legal' | 'business' | 'end_user' | 'executive';
  responsible: boolean;
  accountable: boolean;
  consulted: boolean;
  informed: boolean;
  reasoning: string;
  approval_required: boolean;
  estimated_effort_hours?: number;
}

interface StakeholderAnalysis {
  stakeholders: StakeholderRole[];
  approval_chain: string[];
  bottleneck_risk: 'low' | 'medium' | 'high';
  total_approvers: number;
  cross_department_count: number;
  estimated_approval_days: number;
  batching_suggestions?: string[];
}

interface StakeholderRACIMatrixProps {
  opportunity: {
    id: number;
    title: string;
    integration_complexity_score?: number;
    regulatory_risk_score?: number;
    governance_complexity_score?: number;
    affected_steps?: any[];
    compliance_requirements?: any;
    stakeholder_analysis?: StakeholderAnalysis;
  };
  isExpanded?: boolean;
}

const StakeholderRACIMatrix: React.FC<StakeholderRACIMatrixProps> = ({
  opportunity,
  isExpanded: initialExpanded = false
}) => {
  const [isExpanded, setIsExpanded] = useState(initialExpanded);

  const generateStakeholders = useMemo((): StakeholderAnalysis => {
    if (opportunity.stakeholder_analysis) {
      return opportunity.stakeholder_analysis;
    }

    const stakeholders: StakeholderRole[] = [];
    const integrationScore = opportunity.integration_complexity_score || 5;
    const regulatoryScore = opportunity.regulatory_risk_score || 5;
    const governanceScore = opportunity.governance_complexity_score || 5;
    const affectedSteps = opportunity.affected_steps || [];
    const changeManagement = opportunity.compliance_requirements?.change_management;

    const departments = new Set<string>();
    affectedSteps.forEach((step: any) => {
      if (step.department) departments.add(step.department);
      if (step.owner) departments.add(step.owner);
    });

    const processOwner = affectedSteps[0]?.owner || 'Process Owner';
    stakeholders.push({
      id: 'process_owner',
      name: processOwner,
      department: affectedSteps[0]?.department || 'Operations',
      role_type: 'process_owner',
      responsible: true,
      accountable: true,
      consulted: false,
      informed: false,
      reasoning: 'Owns the process being optimized and is accountable for outcomes',
      approval_required: true,
      estimated_effort_hours: 8
    });

    if (integrationScore >= 5) {
      stakeholders.push({
        id: 'it_director',
        name: 'IT Director',
        department: 'Information Technology',
        role_type: 'it',
        responsible: integrationScore >= 7,
        accountable: false,
        consulted: true,
        informed: integrationScore < 5,
        reasoning: integrationScore >= 7
          ? 'High integration complexity requires IT leadership involvement'
          : 'System integrations require IT consultation',
        approval_required: integrationScore >= 7,
        estimated_effort_hours: integrationScore >= 7 ? 16 : 4
      });
    }

    if (integrationScore >= 7) {
      stakeholders.push({
        id: 'enterprise_architect',
        name: 'Enterprise Architect',
        department: 'Information Technology',
        role_type: 'it',
        responsible: false,
        accountable: false,
        consulted: true,
        informed: false,
        reasoning: 'Complex integrations require architecture review',
        approval_required: false,
        estimated_effort_hours: 8
      });
    }

    if (regulatoryScore >= 5) {
      stakeholders.push({
        id: 'compliance_officer',
        name: 'Compliance Officer',
        department: 'Compliance',
        role_type: 'compliance',
        responsible: false,
        accountable: false,
        consulted: true,
        informed: regulatoryScore < 5,
        reasoning: regulatoryScore >= 7
          ? 'High regulatory risk requires compliance sign-off'
          : 'Regulatory considerations require compliance review',
        approval_required: regulatoryScore >= 7,
        estimated_effort_hours: regulatoryScore >= 7 ? 12 : 4
      });
    }

    if (regulatoryScore >= 8) {
      stakeholders.push({
        id: 'legal_counsel',
        name: 'Legal Counsel',
        department: 'Legal',
        role_type: 'legal',
        responsible: false,
        accountable: false,
        consulted: true,
        informed: false,
        reasoning: 'Critical regulatory implications require legal review',
        approval_required: true,
        estimated_effort_hours: 8
      });
    }

    if (governanceScore >= 7) {
      stakeholders.push({
        id: 'coo',
        name: 'COO / Operations Executive',
        department: 'Executive',
        role_type: 'executive',
        responsible: false,
        accountable: true,
        consulted: false,
        informed: false,
        reasoning: 'High governance complexity requires executive oversight',
        approval_required: true,
        estimated_effort_hours: 2
      });
    }

    if (changeManagement?.trainingRequirements?.requiredRoles?.length > 0) {
      stakeholders.push({
        id: 'training_lead',
        name: 'Training & Development Lead',
        department: 'Human Resources',
        role_type: 'business',
        responsible: true,
        accountable: false,
        consulted: false,
        informed: false,
        reasoning: 'Training requirements identified for affected roles',
        approval_required: false,
        estimated_effort_hours: 16
      });
    }

    const affectedUserCount = affectedSteps.length * 5;
    if (affectedUserCount > 0) {
      stakeholders.push({
        id: 'end_users',
        name: `Affected End Users (Est. ${affectedUserCount})`,
        department: 'Various',
        role_type: 'end_user',
        responsible: false,
        accountable: false,
        consulted: affectedUserCount <= 20,
        informed: true,
        reasoning: 'Users impacted by process changes need communication',
        approval_required: false,
        estimated_effort_hours: 0
      });
    }

    const approvalChain = stakeholders
      .filter(s => s.approval_required)
      .map(s => s.name);

    const totalApprovers = approvalChain.length;
    const crossDepartmentCount = departments.size;

    let bottleneckRisk: 'low' | 'medium' | 'high' = 'low';
    if (totalApprovers >= 4 || crossDepartmentCount >= 4) {
      bottleneckRisk = 'high';
    } else if (totalApprovers >= 2 || crossDepartmentCount >= 2) {
      bottleneckRisk = 'medium';
    }

    const estimatedApprovalDays = totalApprovers * 3 + (crossDepartmentCount > 2 ? 5 : 0);

    return {
      stakeholders,
      approval_chain: approvalChain,
      bottleneck_risk: bottleneckRisk,
      total_approvers: totalApprovers,
      cross_department_count: crossDepartmentCount,
      estimated_approval_days: estimatedApprovalDays
    };
  }, [opportunity]);

  const getRoleIcon = (roleType: string) => {
    switch (roleType) {
      case 'process_owner': return <Briefcase className="w-4 h-4" />;
      case 'it': return <Laptop className="w-4 h-4" />;
      case 'compliance': return <Shield className="w-4 h-4" />;
      case 'legal': return <Scale className="w-4 h-4" />;
      case 'executive': return <Building2 className="w-4 h-4" />;
      case 'end_user': return <Users className="w-4 h-4" />;
      default: return <UserCog className="w-4 h-4" />;
    }
  };

  const getBottleneckColor = (risk: string) => {
    switch (risk) {
      case 'high': return 'bg-red-100 text-red-800 border-red-300';
      case 'medium': return 'bg-yellow-100 text-yellow-800 border-yellow-300';
      default: return 'bg-green-100 text-green-800 border-green-300';
    }
  };

  const raciCounts = useMemo(() => {
    const counts = { R: 0, A: 0, C: 0, I: 0 };
    generateStakeholders.stakeholders.forEach(s => {
      if (s.responsible) counts.R++;
      if (s.accountable) counts.A++;
      if (s.consulted) counts.C++;
      if (s.informed) counts.I++;
    });
    return counts;
  }, [generateStakeholders]);

  return (
    <div className="border border-indigo-200 rounded-lg bg-gradient-to-br from-indigo-50 to-purple-50">
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="w-full px-4 py-3 flex items-center justify-between hover:bg-indigo-100/50 transition-colors rounded-lg"
      >
        <div className="flex items-center gap-3">
          <Users className="w-5 h-5 text-indigo-600" />
          <span className="font-semibold text-indigo-900">Stakeholder & Change Management</span>
          <div className="flex gap-2 ml-2">
            <span className="px-2 py-0.5 bg-blue-100 text-blue-700 text-xs font-medium rounded">
              R:{raciCounts.R}
            </span>
            <span className="px-2 py-0.5 bg-purple-100 text-purple-700 text-xs font-medium rounded">
              A:{raciCounts.A}
            </span>
            <span className="px-2 py-0.5 bg-amber-100 text-amber-700 text-xs font-medium rounded">
              C:{raciCounts.C}
            </span>
            <span className="px-2 py-0.5 bg-gray-100 text-gray-700 text-xs font-medium rounded">
              I:{raciCounts.I}
            </span>
          </div>
          {generateStakeholders.bottleneck_risk !== 'low' && (
            <span className={`px-2 py-0.5 text-xs font-medium rounded border ${getBottleneckColor(generateStakeholders.bottleneck_risk)}`}>
              {generateStakeholders.bottleneck_risk === 'high' ? '⚠️ High' : '⚡ Medium'} Approval Risk
            </span>
          )}
        </div>
        {isExpanded ? (
          <ChevronUp className="w-5 h-5 text-indigo-600" />
        ) : (
          <ChevronDown className="w-5 h-5 text-indigo-600" />
        )}
      </button>

      {isExpanded && (
        <div className="px-4 pb-4 space-y-4">
          <div className="grid grid-cols-4 gap-3">
            <div className="bg-white rounded-lg p-3 border border-gray-200 text-center">
              <div className="text-2xl font-bold text-indigo-600">{generateStakeholders.total_approvers}</div>
              <div className="text-xs text-gray-600">Approvers Required</div>
            </div>
            <div className="bg-white rounded-lg p-3 border border-gray-200 text-center">
              <div className="text-2xl font-bold text-purple-600">{generateStakeholders.cross_department_count}</div>
              <div className="text-xs text-gray-600">Departments Involved</div>
            </div>
            <div className="bg-white rounded-lg p-3 border border-gray-200 text-center">
              <div className="text-2xl font-bold text-amber-600">{generateStakeholders.estimated_approval_days}</div>
              <div className="text-xs text-gray-600">Est. Approval Days</div>
            </div>
            <div className="bg-white rounded-lg p-3 border border-gray-200 text-center">
              <div className={`text-2xl font-bold ${
                generateStakeholders.bottleneck_risk === 'high' ? 'text-red-600' :
                generateStakeholders.bottleneck_risk === 'medium' ? 'text-yellow-600' : 'text-green-600'
              }`}>
                {generateStakeholders.bottleneck_risk.toUpperCase()}
              </div>
              <div className="text-xs text-gray-600">Bottleneck Risk</div>
            </div>
          </div>

          <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
            <div className="px-4 py-2 bg-gray-50 border-b border-gray-200">
              <h4 className="font-semibold text-gray-800 flex items-center gap-2">
                <UserCheck className="w-4 h-4" />
                RACI Matrix
              </h4>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="bg-gray-50 text-xs">
                    <th className="px-4 py-2 text-left font-semibold text-gray-700">Stakeholder</th>
                    <th className="px-4 py-2 text-left font-semibold text-gray-700">Department</th>
                    <th className="px-3 py-2 text-center font-semibold text-blue-700" title="Responsible - Does the work">
                      <div className="flex flex-col items-center">
                        <span>R</span>
                        <span className="text-[10px] font-normal text-gray-500">Responsible</span>
                      </div>
                    </th>
                    <th className="px-3 py-2 text-center font-semibold text-purple-700" title="Accountable - Final decision authority">
                      <div className="flex flex-col items-center">
                        <span>A</span>
                        <span className="text-[10px] font-normal text-gray-500">Accountable</span>
                      </div>
                    </th>
                    <th className="px-3 py-2 text-center font-semibold text-amber-700" title="Consulted - Provides input">
                      <div className="flex flex-col items-center">
                        <span>C</span>
                        <span className="text-[10px] font-normal text-gray-500">Consulted</span>
                      </div>
                    </th>
                    <th className="px-3 py-2 text-center font-semibold text-gray-700" title="Informed - Needs to know">
                      <div className="flex flex-col items-center">
                        <span>I</span>
                        <span className="text-[10px] font-normal text-gray-500">Informed</span>
                      </div>
                    </th>
                    <th className="px-4 py-2 text-left font-semibold text-gray-700">Reasoning</th>
                  </tr>
                </thead>
                <tbody>
                  {generateStakeholders.stakeholders.map((stakeholder, idx) => (
                    <tr key={stakeholder.id} className={idx % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <span className="text-gray-500">{getRoleIcon(stakeholder.role_type)}</span>
                          <span className="font-medium text-gray-800">{stakeholder.name}</span>
                          {stakeholder.approval_required && (
                            <span className="px-1.5 py-0.5 bg-red-100 text-red-700 text-[10px] font-medium rounded">
                              APPROVAL
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-600">{stakeholder.department}</td>
                      <td className="px-3 py-3 text-center">
                        {stakeholder.responsible && (
                          <span className="inline-flex items-center justify-center w-6 h-6 bg-blue-100 text-blue-700 rounded-full font-bold text-sm">
                            R
                          </span>
                        )}
                      </td>
                      <td className="px-3 py-3 text-center">
                        {stakeholder.accountable && (
                          <span className="inline-flex items-center justify-center w-6 h-6 bg-purple-100 text-purple-700 rounded-full font-bold text-sm">
                            A
                          </span>
                        )}
                      </td>
                      <td className="px-3 py-3 text-center">
                        {stakeholder.consulted && (
                          <span className="inline-flex items-center justify-center w-6 h-6 bg-amber-100 text-amber-700 rounded-full font-bold text-sm">
                            C
                          </span>
                        )}
                      </td>
                      <td className="px-3 py-3 text-center">
                        {stakeholder.informed && (
                          <span className="inline-flex items-center justify-center w-6 h-6 bg-gray-200 text-gray-700 rounded-full font-bold text-sm">
                            I
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-xs text-gray-600 max-w-xs">{stakeholder.reasoning}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {generateStakeholders.approval_chain.length > 0 && (
            <div className="bg-white rounded-lg border border-gray-200 p-4">
              <h4 className="font-semibold text-gray-800 flex items-center gap-2 mb-3">
                <MessageSquare className="w-4 h-4" />
                Approval Chain
              </h4>
              <div className="flex items-center flex-wrap gap-2">
                {generateStakeholders.approval_chain.map((approver, idx) => (
                  <React.Fragment key={approver}>
                    <div className="px-3 py-2 bg-gradient-to-r from-indigo-100 to-purple-100 border border-indigo-200 rounded-lg text-sm font-medium text-indigo-800">
                      {idx + 1}. {approver}
                    </div>
                    {idx < generateStakeholders.approval_chain.length - 1 && (
                      <span className="text-gray-400">→</span>
                    )}
                  </React.Fragment>
                ))}
              </div>
              <p className="text-xs text-gray-500 mt-2">
                Sequential approval required. Consider parallel approvals where possible to reduce timeline.
              </p>
            </div>
          )}

          <div className="bg-amber-50 rounded-lg border border-amber-200 p-4">
            <h4 className="font-semibold text-amber-800 flex items-center gap-2 mb-2">
              <Bell className="w-4 h-4" />
              Communication Plan Triggers
            </h4>
            <ul className="text-sm text-amber-700 space-y-1">
              <li>• Announce to affected users 2 weeks before go-live</li>
              <li>• Send training schedule 1 week before implementation</li>
              <li>• Notify stakeholders immediately upon approval completion</li>
              {generateStakeholders.cross_department_count > 2 && (
                <li>• Cross-department coordination meeting required weekly</li>
              )}
            </ul>
          </div>

          <div className="flex items-start gap-2 p-3 bg-blue-50 rounded-lg border border-blue-200">
            <HelpCircle className="w-4 h-4 text-blue-600 mt-0.5 flex-shrink-0" />
            <p className="text-xs text-blue-700">
              <strong>RACI Legend:</strong> R = Responsible (does the work), A = Accountable (final authority),
              C = Consulted (provides input), I = Informed (needs to know).
              Each task should have exactly one Accountable person.
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default StakeholderRACIMatrix;
